/**
 * 이미지 업로드/삭제 관련 작업 목차
 * 
 * - uploadImage() - 단일 이미지 업로드 (자동 압축)
 * - deleteImage() - 이미지 삭제
 * - uploadMultipleImages() - 여러 이미지 업로드 (자동 압축)
 * - uploadCommentImage() - 댓글 이미지 업로드 (자동 압축)
 * - uploadBoardImages() - 게시판 이미지 업로드 (자동 압축)
 * - uploadProfileImage() - 프로필 이미지 업로드 (자동 압축)
 * - uploadVenueImage() - 공연장 이미지 업로드 (자동 압축)
 * 
 * 모든 업로드 함수는 자동으로 이미지를 압축합니다.
 * - 최대 크기: 1500x3000
 * - 최대 파일 크기: 10MB
 * - 자동 품질 조정
 */

import apiClient from "@/utils/api";
import { compressImage } from "@/utils/imageCompressor";

/**
 * 이미지 업로드 (자동 압축)
 * @param {File} file - 이미지 파일
 * @param {string} path - Storage 경로 (예: 'profile', 'poster', 'artist')
 * @param {string} fileName - 파일명 (선택사항, 지정하지 않으면 자동 생성)
 * @returns {Promise<string>} 다운로드 URL
 */
export async function uploadImage(file, path, fileName = null) {
  try {
    // 이미지 자동 압축 (10MB 이하, 1500x3000 이내)
    const compressedFile = await compressImage(file);
    
    const formData = new FormData();
    formData.append("image", compressedFile);
    formData.append("path", path);
    
    if (fileName) {
      formData.append("fileName", fileName);
    }

    const response = await apiClient.post("/api/storage/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.data.url;
  } catch (error) {
    console.error("이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 이미지 삭제
 * @param {string} path - Storage 경로
 * @param {string} fileName - 파일명
 * @returns {Promise<void>}
 */
export async function deleteImage(path, fileName) {
  try {
    await apiClient.delete("/api/storage", {
      data: { path, fileName },
    });
  } catch (error) {
    console.error("이미지 삭제 오류:", error);
    throw error;
  }
}

/**
 * 여러 이미지 업로드 (자동 압축)
 * @param {Array<File>} files - 이미지 파일 배열
 * @param {string} path - Storage 경로
 * @returns {Promise<Array<string>>} 다운로드 URL 배열
 */
export async function uploadMultipleImages(files, path) {
  try {
    // 모든 이미지 압축
    const compressedFiles = await Promise.all(
      files.map(file => compressImage(file))
    );
    
    const formData = new FormData();
    
    compressedFiles.forEach((file) => {
      formData.append("images", file);
    });
    
    formData.append("path", path);

    const response = await apiClient.post("/api/storage/upload-multiple", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // 서버에서는 { fileName, url } 객체 배열을 반환
    return response.data.data.map(item => item.url);
  } catch (error) {
    console.error("여러 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 댓글 이미지 업로드 (자동 압축)
 * @param {File} imageFile - 이미지 파일
 * @param {string} userEmail - 사용자 이메일
 * @returns {Promise<string>} 다운로드 URL
 */
export async function uploadCommentImage(imageFile, userEmail) {
  try {
    // 이미지 자동 압축
    const compressedFile = await compressImage(imageFile);
    
    const formData = new FormData();
    formData.append("image", compressedFile);
    formData.append("userEmail", userEmail);

    const response = await apiClient.post("/api/storage/comment-image", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return response.data.data.url;
  } catch (error) {
    console.error("댓글 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 게시판 이미지 업로드 (자동 압축)
 * @param {Array<File>} imageFiles - 이미지 파일 배열
 * @returns {Promise<Array<string>>} 다운로드 URL 배열
 */
export async function uploadBoardImages(imageFiles) {
  try {
    // 모든 이미지 압축
    const compressedFiles = await Promise.all(
      imageFiles.map(file => compressImage(file))
    );
    
    const formData = new FormData();
    
    compressedFiles.forEach((file) => {
      formData.append("images", file);
    });

    const response = await apiClient.post("/api/storage/board-images", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    // 서버에서는 { fileName, url } 객체 배열을 반환
    return response.data.data.map(item => item.url);
  } catch (error) {
    console.error("게시판 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 프로필 이미지 업로드 (자동 압축, Storage에만 업로드)
 * 
 * 주의: 이 함수는 Storage에만 업로드하며, Firestore 업데이트는 하지 않습니다.
 * 
 * 사용 사례:
 * - new-artist 모드: 신규 아티스트 등록 시 임시 저장용
 *   (Firestore 저장은 ArtistForm의 폼 제출 시 처리)
 * 
 * 일반적인 프로필 이미지 업로드는 다음 함수를 사용하세요:
 * - 사용자: setProfileImage() in user.js (Storage + Firestore 모두 처리)
 * - 아티스트: setArtistImage() in artist.js (Storage + Firestore 모두 처리)
 * 
 * @param {File} imageFile - 이미지 파일
 * @param {string} uniqueId - 고유 ID (이메일 또는 아티스트명, 현재는 사용되지 않음)
 * @param {string} mode - 모드 ('user' 또는 'artist'), 경로 결정에 사용
 * @returns {Promise<{downloadURL: string, fileName: string}>}
 */
export async function uploadProfileImage(imageFile, uniqueId, mode = "user") {
  try {
    // 이미지 자동 압축
    const compressedFile = await compressImage(imageFile);
    
    const formData = new FormData();
    
    // 한글 파일명 문제 방지: 원본 파일명을 안전한 파일명으로 변환
    const originalFileName = compressedFile.name;
    const fileExtension = originalFileName.split('.').pop() || 'jpg';
    // 타임스탬프 기반 파일명 생성 (한글 파일명 문제 방지)
    const safeFileName = `${Math.random().toString(36).substring(2, 9)}.${fileExtension}`;
    
    // Blob으로 파일명 변경하여 업로드
    const renamedFile = new File([compressedFile], safeFileName, { type: compressedFile.type });
    formData.append("image", renamedFile);
    
    const path = mode === "user" ? "profile" : "artist";
    formData.append("path", path);

    const response = await apiClient.post("/api/storage/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return {
      downloadURL: response.data.data.url,
      fileName: response.data.data.fileName,
    };
  } catch (error) {
    console.error("프로필 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * 공연장 이미지 업로드 (자동 압축)
 * @param {File} imageFile - 이미지 파일
 * @param {string} venueName - 공연장 이름
 * @returns {Promise<{downloadURL: string, fileName: string}>}
 */
export async function uploadVenueImage(imageFile) {
  try {
    // 이미지 자동 압축
    const compressedFile = await compressImage(imageFile);
    
    const formData = new FormData();
    formData.append("image", compressedFile);
    formData.append("path", "venue");

    const response = await apiClient.post("/api/storage/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });

    return {
      downloadURL: response.data.data.url,
      fileName: response.data.data.fileName,
    };
  } catch (error) {
    console.error("공연장 이미지 업로드 오류:", error);
    throw error;
  }
}

/**
 * Storage 파일 복사
 * @param {string} sourcePath - 원본 파일 경로 (예: 'profile/filename.jpg')
 * @param {string} destPath - 대상 파일 경로 (예: 'artist/filename.jpg')
 * @returns {Promise<void>}
 */
export async function copyStorageFile(sourcePath, destPath) {
  try {
    await apiClient.post("/api/storage/copy", {
      sourcePath,
      destPath,
    });
  } catch (error) {
    console.error("파일 복사 오류:", error);
    throw error;
  }
}
